import React from 'react';
import {
  Calendar, DollarSign, Zap, BookOpen, Brain, ListTodo,
  ChevronRight, ArrowRight
} from 'lucide-react';

const features = [
  {
    icon: Zap,
    title: 'Habit Tracker',
    description: 'Build consistency with daily habit tracking, streak calculations, and 30-day completion rates. Color-coded for quick visual feedback.',
    color: 'bg-amber-100',
    iconColor: 'text-amber-600'
  },
  {
    icon: Calendar,
    title: 'Calendar & Day Planner',
    description: 'Monthly calendar with day highlighting in 7 colors. Click any day to plan your schedule, tasks, and notes with hourly breakdowns.',
    color: 'bg-blue-100',
    iconColor: 'text-blue-600'
  },
  {
    icon: BookOpen,
    title: 'Weekly Journal',
    description: 'Capture thoughts, reflections, and ideas week by week. Timeline view with timestamps so you never lose an important insight.',
    color: 'bg-emerald-100',
    iconColor: 'text-emerald-600'
  },
  {
    icon: Brain,
    title: 'Brain Dump',
    description: 'Quickly capture ideas on colorful cards. Perfect for brainstorming sessions when thoughts come faster than you can organize them.',
    color: 'bg-fuchsia-100',
    iconColor: 'text-fuchsia-600'
  },
  {
    icon: ListTodo,
    title: 'To-Do List',
    description: 'Global task management with priority levels and energy-based categories. Filter by status to focus on what matters now.',
    color: 'bg-indigo-100',
    iconColor: 'text-indigo-600'
  },
  {
    icon: DollarSign,
    title: 'Financial Tracking',
    description: 'Track revenue and expenses week by week. Visual progress bars and running totals keep your finances clear at a glance.',
    color: 'bg-green-100',
    iconColor: 'text-green-600'
  }
];

const steps = [
  {
    number: '01',
    title: 'Sign Up',
    description: 'Create your free account in seconds. Your data syncs across all your devices.'
  },
  {
    number: '02',
    title: 'Plan Your Week',
    description: 'Set goals, track habits, manage finances, and journal — all in one focused view.'
  },
  {
    number: '03',
    title: 'Achieve More',
    description: 'Review your progress with the yearly summary. See how far you\'ve come across all 52 weeks.'
  }
];

export default function LandingPage({ onGetStarted, onLogin }) {
  return (
    <div className="min-h-screen bg-white">
      {/* ─── Navbar ─────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Calendar className="text-white" size={18} />
            </div>
            <span className="font-bold text-lg text-gray-800">52 Week Planner</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onLogin}
              className="text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors px-3 py-2"
            >
              Log In
            </button>
            <button
              onClick={onGetStarted}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium text-sm px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ───────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-300 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full border border-white/20 mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-white/90 text-sm font-medium">Plan your entire year in one place</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
            Your Year.{' '}
            <span className="text-yellow-300">52 Weeks.</span>
            <br />
            One Powerful Planner.
          </h1>
          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Track habits, manage finances, plan your calendar, journal your journey,
            and set weekly goals — all synced to the cloud and accessible anywhere.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onGetStarted}
              className="flex items-center gap-2 bg-white text-indigo-700 font-bold px-8 py-3.5 rounded-xl hover:bg-gray-50 transition-colors shadow-lg shadow-black/10 text-lg"
            >
              Start Planning Free
              <ArrowRight size={20} />
            </button>
            <button
              onClick={onLogin}
              className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-medium px-8 py-3.5 rounded-xl hover:bg-white/20 transition-colors border border-white/20"
            >
              I have an account
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ─── Features Grid ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4">
            Everything You Need to Own Your Year
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Six powerful tools working together in one beautiful interface.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow p-7 border border-gray-100 group"
            >
              <div className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                <feature.icon className={feature.iconColor} size={28} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{feature.title}</h3>
              <p className="text-gray-500 leading-relaxed text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── How It Works ───────────────────────────────────────────────── */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4">
              Get Started in 3 Steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-blue-200">
                  <span className="text-white font-extrabold text-xl">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Stats / Trust ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              Ready to Plan Your Best Year?
            </h2>
            <p className="text-white/70 text-lg max-w-lg mx-auto mb-8">
              Join now and start tracking your goals, habits, and finances — week by week, all year long.
            </p>
            <button
              onClick={onGetStarted}
              className="inline-flex items-center gap-2 bg-white text-indigo-700 font-bold px-8 py-3.5 rounded-xl hover:bg-gray-50 transition-colors shadow-lg text-lg"
            >
              Create Your Free Account
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* ─── Footer ─────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
              <Calendar className="text-white" size={14} />
            </div>
            <span className="font-semibold text-gray-700 text-sm">52 Week Planner 2026</span>
          </div>
          <p className="text-gray-400 text-sm">Plan smarter. Achieve more.</p>
        </div>
      </footer>
    </div>
  );
}
